import { IRequest, RequestStatus, RequestType, ServiceType } from './types';
import { PERMISSION } from '../../../components/investigation/share/user_roles';

/**
 * SHER-366 — gating policy for the modify/cancel-request UI controls.
 *
 * Pure predicates mirroring the backend guards so a control is only offered
 * when the API would accept the action. The server remains the source of truth;
 * these just keep the UI from presenting obviously-blocked actions.
 */

// A request is closed (no in-place mutation / cancel) in any of these states.
export const REQUEST_TERMINAL_STATUSES = [
    RequestStatus.COMPLETED,
    RequestStatus.CANCELED,
    RequestStatus.CANCELED_BY_USER,
    RequestStatus.DENIED,
    RequestStatus.EXPIRED,
    // SHER-908: the patient already has the material — mirrors the API's TERMINAL_STATUSES.
    RequestStatus.MATERIAL_DELIVERED,
];

export function isRequestTerminal(request: IRequest): boolean {
    return REQUEST_TERMINAL_STATUSES.includes(request.status);
}

// Mirrors cancelRequest: staff CANCEL_REQUESTS permission and the request still
// open. SHER-334: surgery is cancelled through this same unified path (the old
// surgery state machine rejected CANCELED from PENDING_PAYMENT), so it is no
// longer excluded here — only the terminal-state gate applies.
export function canCancelRequest(request: IRequest, permissions: string[]): boolean {
    return permissions.includes(PERMISSION.CANCEL_REQUESTS)
        && !isRequestTerminal(request);
}

// Mirrors the add/remove-service guards: MODIFY_REQUESTS, request still open, and
// — for surgery — not past check-in. (Paid-item removal is blocked server-side;
// the client item shape carries no payment info, so the API surfaces that error.)
export function canModifyRequestServices(request: IRequest, permissions: string[]): boolean {
    return permissions.includes(PERMISSION.MODIFY_REQUESTS)
        && !isRequestTerminal(request)
        && !(request.type === RequestType.SURGERY && request.status === RequestStatus.CHECKED_IN);
}

// A service item can be removed only when more than one remains (the last service
// must be removed by cancelling the request instead — matches the backend guard).
export function canRemoveServiceItem(request: IRequest, permissions: string[]): boolean {
    return canModifyRequestServices(request, permissions)
        && (request.requestsServiceInvestigation?.length ?? 0) > 1;
}

// SHER-411 — radiology (IMAGING) and cordonnerie (SHOE) don't upload a report/result,
// so the lab "upload results -> COMPLETED" path never fires and their requests get stuck
// in ACCEPTED. Offer a manual "mark as performed" only for those two types, only on a
// paid (ACCEPTED) request, and only with UPDATE_LAB_REQUESTS (the same authority that
// completes lab/radiology/cordonnerie results). Mirrors the backend guard in
// completeManualRequest; the server stays the source of truth.
const MANUAL_COMPLETION_SERVICE_TYPES = [ServiceType.IMAGING, ServiceType.SHOE];

// SHER-908 — only cordonnerie hands physical material to the patient, and only after the
// shoe was actually made (COMPLETED). Reuses UPDATE_LAB_REQUESTS rather than a new
// permission: the authority to record that the act was performed is the authority to
// record that it was handed over, and a new permission would need a Seeding migration.
// Mirrors deliverMaterialRequest; the server stays the source of truth.
export function canMarkMaterialDelivered(request: IRequest, requestServiceType: ServiceType | undefined, permissions: string[]): boolean {
    return permissions.includes(PERMISSION.UPDATE_LAB_REQUESTS)
        && requestServiceType === ServiceType.SHOE
        && request.status === RequestStatus.COMPLETED;
}
export function canManuallyCompleteRequest(request: IRequest, requestServiceType: ServiceType | undefined, permissions: string[]): boolean {
    return permissions.includes(PERMISSION.UPDATE_LAB_REQUESTS)
        && requestServiceType !== undefined
        && MANUAL_COMPLETION_SERVICE_TYPES.includes(requestServiceType)
        && request.status === RequestStatus.ACCEPTED;
}

/**
 * Whether the surgical status row has anything to offer.
 *
 * The three clauses, unchanged from SHER-236: only surgical requests have a server-driven
 * state machine; a completed one has nowhere left to go; and an empty transition list with
 * no outstanding balance is a real answer, not a missing one — a cancelled request, or one
 * whose next move is a payment nobody owes.
 *
 * Gated on the request's own type, not the route's ServiceType: that is the condition the
 * API uses to attach nextStatuses/owesPayment, so inside this guard their absence is a
 * contract fault rather than a state. No permission check, as before — the server decides
 * which transitions the user is offered.
 */
export function hasStatusActions(request: IRequest): boolean {
    if (request.type !== RequestType.SURGERY || request.status === RequestStatus.COMPLETED) {
        return false;
    }
    return (request.nextStatuses ?? []).length > 0 || (request.owesPayment ?? false);
}

/**
 * SHER-1062 — the step the request-management panel offers as "Prochaine action".
 *
 * Picked from the existing workflow only, never a new transition: cordonnerie COMPLETED →
 * material delivered (SHER-908), radiology/cordonnerie ACCEPTED → mark as performed
 * (SHER-411), surgery → whatever the API served (SHER-236). The three are mutually exclusive
 * by status/type; the order only makes that explicit. Laboratory has no button transition
 * (uploading the results completes it), so it gets null.
 */
export type RequestNextAction = 'deliver' | 'complete' | 'surgery';

export function requestNextAction(request: IRequest, requestServiceType: ServiceType | undefined, permissions: string[]): RequestNextAction | null {
    if (canMarkMaterialDelivered(request, requestServiceType, permissions)) return 'deliver';
    if (canManuallyCompleteRequest(request, requestServiceType, permissions)) return 'complete';
    if (hasStatusActions(request)) return 'surgery';
    return null;
}

const NEXT_ACTION_NOTE: Record<RequestNextAction, string> = {
    deliver: 'pages.hospital.services.request.deliver.note',
    complete: 'pages.hospital.services.request.complete.note',
    surgery: 'pages.hospital.services.request.status_update.note',
};

// What the next action writes down (status, date, acting user — every one of these endpoints
// writes a status-log row). Going to billing records nothing, so a surgical request with only
// the billing link on offer gets no note.
export function requestNextActionNoteId(request: IRequest, action: RequestNextAction | null): string | null {
    if (action === null) return null;
    if (action === 'surgery' && (request.nextStatuses ?? []).length === 0) return null;
    return NEXT_ACTION_NOTE[action];
}

// SHER-363 #2 — stable error codes returned by the modify/cancel endpoints
// (mirror of the API's RequestModificationErrorCode). The API sends a numeric
// code; the client maps it to a localized message so raw English strings never
// reach <Translate id> (which rendered them as "Missing translationId ...").
export enum RequestModificationErrorCode {
    REASON_REQUIRED = 1,
    REQUEST_NOT_FOUND = 2,
    TERMINAL_STATE = 3,
    SURGERY_CHECKED_IN = 4,
    SERVICE_NOT_FOUND = 5,
    LAST_SERVICE = 6,
    SERVICE_PAID = 7,
    AT_LEAST_ONE_SERVICE = 8,
    SERVICE_DOESNT_EXIST = 9,
    AMOUNT_NOT_FOUND = 10,
    BILL_NOT_CREATED = 11,
    SURGERY_CANCEL_WORKFLOW = 12,
    REQUEST_HAS_PAID_SERVICE = 13,
}

const ERROR_CODE_TRANSLATION: Record<RequestModificationErrorCode, string> = {
    [RequestModificationErrorCode.REASON_REQUIRED]: 'pages.hospital.services.request.error.reason_required',
    [RequestModificationErrorCode.REQUEST_NOT_FOUND]: 'pages.hospital.services.request.error.request_not_found',
    [RequestModificationErrorCode.TERMINAL_STATE]: 'pages.hospital.services.request.error.terminal_state',
    [RequestModificationErrorCode.SURGERY_CHECKED_IN]: 'pages.hospital.services.request.error.surgery_checked_in',
    [RequestModificationErrorCode.SERVICE_NOT_FOUND]: 'pages.hospital.services.request.error.service_not_found',
    [RequestModificationErrorCode.LAST_SERVICE]: 'pages.hospital.services.request.error.last_service',
    [RequestModificationErrorCode.SERVICE_PAID]: 'pages.hospital.services.request.error.service_paid',
    [RequestModificationErrorCode.AT_LEAST_ONE_SERVICE]: 'pages.hospital.services.request.error.at_least_one_service',
    [RequestModificationErrorCode.SERVICE_DOESNT_EXIST]: 'pages.hospital.services.request.error.service_doesnt_exist',
    [RequestModificationErrorCode.AMOUNT_NOT_FOUND]: 'pages.hospital.services.request.error.amount_not_found',
    [RequestModificationErrorCode.BILL_NOT_CREATED]: 'pages.hospital.services.request.error.bill_not_created',
    [RequestModificationErrorCode.SURGERY_CANCEL_WORKFLOW]: 'pages.hospital.services.request.error.surgery_cancel_workflow',
    [RequestModificationErrorCode.REQUEST_HAS_PAID_SERVICE]: 'pages.hospital.services.request.error.request_has_paid_service',
};

// Map an API error (the rejected {status, errorCode, errorMessage} body) to a
// translation key. Falls back to the generic error so an unmapped/old code never
// leaks a raw string into the snackbar.
export function requestModificationErrorKey(error: { errorCode?: number } | undefined): string {
    const code = error?.errorCode as RequestModificationErrorCode | undefined;
    return (code !== undefined && ERROR_CODE_TRANSLATION[code]) || 'general.error';
}

// SHER-363 #8 — per-module terminology. "service/servicio" is ambiguous (reads as a
// hospital department), so the modify controls say test (lab) / exam (radiology) /
// surgical act (surgery). Maps a request's ServiceType to its translation subtree.
type TerminologyKey = 'add_section' | 'add_modal' | 'select_label' | 'modify_section' | 'remove_title' | 'remove_modal' | 'items_section';

export function requestTerminologyKey(serviceType: ServiceType | undefined, key: TerminologyKey): string {
    const module = serviceType === ServiceType.LABORATORY ? 'lab'
        : serviceType === ServiceType.IMAGING ? 'imaging'
        : serviceType === ServiceType.SURGERY ? 'surgery'
        : 'default';
    return `pages.hospital.services.request.terminology.${module}.${key}`;
}
